import React, { useState } from "react"
import { useDraggable } from "@dnd-kit/react"
import { Popover } from "react-tiny-popover"
import moment from "moment"
import type Task from "~/types/Task"
import { CheckCircle, MoreVertical, Building2, User, Briefcase, Users } from "lucide-react"
import ConfirmationModal from "~/library/ConfirmationModal"
import EditTask from "./EditTask"
import { useCoordinatedPopover } from "~/hooks/useCoordinatedPopover"
import NavigationLink from "~/library/NavigationLink"

interface DraggableTaskProps {
    task: Task
    status: string
    onDeleteTask: (taskPrefixId: string) => void
    onTaskUpdated?: () => void
}

const DraggableTask: React.FC<DraggableTaskProps> = ({
                                                         task,
                                                         status,
                                                         onDeleteTask,
                                                         onTaskUpdated,
                                                     }) => {
    const { ref } = useDraggable({
        id: task.prefixId,
        type: "task",
        data: {
            taskId: task.prefixId,
            currentStatus: status,
        },
    })

    const [showEditModal, setShowEditModal] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)

    const {
        isOpen: showPopover,
        togglePopover,
        closePopover,
        handleClickOutside,
        toggleButtonRef,
    } = useCoordinatedPopover(`task-${task.prefixId}`)

    const handleEditActionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        setShowEditModal(true)
        closePopover()
    }

    const handleDeleteActionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        setShowDeleteModal(true)
        closePopover()
    }

    const taskTypeLabels: Record<string, string> = {
        call: "Call",
        meeting: "Meeting",
        follow_up: "Follow Up",
        proposal: "Proposal",
        renewal: "Renewal",
    }

    return (
        <div ref={ref} style={{ cursor: 'grab' }}>
            <div className="d-flex align-items-center gap-2 py-2 border-bottom flex-wrap">
                <div style={{ minWidth: "40px" }}>
                    <div
                        className="d-flex align-items-center justify-content-center rounded"
                        style={{ width: "40px", height: "40px", backgroundColor: "rgba(0,0,0,0.1)" }}
                    >
                        <CheckCircle size={20} className="text-muted" />
                    </div>
                </div>

                <div style={{ minWidth: "150px", flex: "1 1 150px" }}>
                    <div className="fw-semibold mb-1">{task.title}</div>
                    <span className="badge bg-secondary font-11">
                        {taskTypeLabels[task.taskType]}
                    </span>
                </div>

                <div style={{ minWidth: "120px", flex: "1 1 120px" }}>
                    {task.deal ? (
                        <div className="d-flex align-items-center gap-1">
                            <Briefcase size={14} className="text-muted flex-shrink-0" />
                            <NavigationLink type="deals" to={`/deals/${task.deal.prefixId}`}>
                                <span className="font-13">{task.deal.name}</span>
                            </NavigationLink>
                        </div>
                    ) : (
                        <span className="text-muted font-13">—</span>
                    )}
                </div>

                <div style={{ minWidth: "120px", flex: "1 1 120px" }}>
                    {task.account ? (
                        <div className="d-flex align-items-center gap-1">
                            <Building2 size={14} className="text-muted flex-shrink-0" />
                            <NavigationLink type="accounts" to={`/accounts/${task.account.prefixId}`}>
                                <span className="font-13">{task.account.name}</span>
                            </NavigationLink>
                        </div>
                    ) : (
                        <span className="text-muted font-13">—</span>
                    )}
                </div>

                <div style={{ minWidth: "120px", flex: "1 1 120px" }}>
                    {task.contact ? (
                        <div className="d-flex align-items-center gap-1">
                            <User size={14} className="text-muted flex-shrink-0" />
                            <NavigationLink type="contacts" to={`/contacts/${task.contact.prefixId}`}>
                                <span className="font-13">
                                    {task.contact.firstName} {task.contact.lastName}
                                </span>
                            </NavigationLink>
                        </div>
                    ) : (
                        <span className="text-muted font-13">—</span>
                    )}
                </div>

                <div style={{ minWidth: "100px", flex: "0 1 100px" }}>
                    {task.user && (
                        <div className="d-flex align-items-center gap-1 mb-1">
                            <Users size={14} className="text-muted flex-shrink-0" />
                            <span className="font-13">{task.user.name}</span>
                        </div>
                    )}
                    <div className="text-muted font-12">
                        {moment(task.createdAt).format("MMM D, YYYY")}
                    </div>
                </div>

                <div style={{ minWidth: "40px" }} className="ms-auto">
                    <Popover
                        isOpen={showPopover}
                        padding={6}
                        positions={["left", "bottom"]}
                        reposition={false}
                        onClickOutside={handleClickOutside}
                        content={() => (
                            <ul
                                className="dropdown-menu"
                                style={{ display: "block", position: "unset", minWidth: "unset" }}
                            >
                                <li>
                                    <button
                                        onClick={handleEditActionClick}
                                        className="dropdown-item"
                                        role="menuitem"
                                        type="button"
                                    >
                                        Edit Task
                                    </button>
                                </li>
                                <li>
                                    <button
                                        onClick={handleDeleteActionClick}
                                        className="dropdown-item text-danger"
                                        role="menuitem"
                                        type="button"
                                    >
                                        Delete Task
                                    </button>
                                </li>
                            </ul>
                        )}
                    >
                        <button
                            ref={toggleButtonRef}
                            type="button"
                            className="btn btn-sm btn-link"
                            onClick={togglePopover}
                            title="Actions"
                        >
                            <MoreVertical size={16} />
                        </button>
                    </Popover>
                </div>
            </div>

            {showDeleteModal && (
                <ConfirmationModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={() => {
                        onDeleteTask(task.prefixId)
                        setShowDeleteModal(false)
                    }}
                    title={`Delete Task "${task.title}"?`}
                    description="This action cannot be undone. Are you sure you want to delete this task?"
                />
            )}

            {showEditModal && (
                <EditTask
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onSuccess={onTaskUpdated}
                    taskPrefixId={task.prefixId}
                />
            )}
        </div>
    )
}

export default DraggableTask
